import type { UserLevel, MenuItem } from '@/types'

export interface MenuItemDef {
  key: string
  label: string
  path: string
  icon?: string
  requiredModule?: string
  requiredFunction?: string
  maxUserLevel: UserLevel
  children?: MenuItemDef[]
}

/**
 * 좌측 사이드바 메뉴의 정적 폴백 목록.
 *
 * 실제 메뉴는 `menuApi.list()`(GET /v1/menu-list)가 권한 DB(hiball_user_management의
 * menu / menu_authorization / role_for_user / menu_for_dept)에서 내려준다. 이 배열은 그 호출이
 * 실패했거나 권한 데이터가 아직 등록되지 않아 빈 목록이 올 때 사이드바가 비지 않도록 쓰는 폴백이다
 * (참조 프로젝트 giants_trackman_analyzer와 동일한 구조).
 */
export const MENU_DEFINITIONS: MenuItemDef[] = [
  {
    key: 'game-manager',
    label: '경기 데이터 관리',
    path: '/game-manager',
    icon: 'database',
    maxUserLevel: 1,
  },
  {
    key: 'batch',
    label: '배치 작업',
    path: '/batch',
    icon: 'setting',
    requiredModule: 'config',
    maxUserLevel: 0,
  },
  {
    key: 'data',
    label: '데이터 관리',
    path: '/data',
    icon: 'database',
    maxUserLevel: 0,
  },
]

/**
 * 권한 DB의 menu 테이블에는 아이콘 정보가 없다. 경로에 포함된 키워드로 아이콘을 붙인다
 * (참조 프로젝트 giants_trackman_analyzer / hanwha_scouting의 Sidebar가 쓰는 것과 같은 방식 —
 *  경로 문자열이 정확히 일치하지 않아도 안전하도록 "부분 문자열 포함" 매칭을 쓴다).
 * 위에서부터 먼저 매칭되는 것을 쓰므로, 더 구체적인 키워드를 앞에 둔다.
 */
const PATH_ICON_KEYWORDS: { keyword: string; icon: string }[] = [
  { keyword: 'game-manager', icon: 'database' },
  { keyword: 'batch', icon: 'setting' },
  { keyword: 'data', icon: 'database' },
]

export function iconForPath(path: string): string | undefined {
  return PATH_ICON_KEYWORDS.find((m) => path.includes(m.keyword))?.icon
}

/** menu.menu_path는 앞에 '/' 없이 저장돼 있다 — 라우트로 쓰려면 붙여준다. */
export function toRoutePath(menuPath: string): string {
  const trimmed = (menuPath ?? '').trim()
  if (!trimmed) return '/'
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

/**
 * 권한 DB에서 받은 평면 메뉴 목록을 사이드바용 트리로 변환한다.
 * parentMenuId가 목록 안에 없으면(= 시스템 자신이 부모) 최상위 메뉴로 취급한다.
 */
export function buildMenuTree(menus: MenuItem[]): MenuItemDef[] {
  const byId = new Map<number, MenuItemDef>()
  const nodes = menus.map((m) => {
    const path = toRoutePath(m.path)
    const node: MenuItemDef = {
      key: String(m.menuId),
      label: m.title,
      path,
      icon: iconForPath(path),
      maxUserLevel: 9,
    }
    byId.set(m.menuId, node)
    return { menu: m, node }
  })

  const roots: MenuItemDef[] = []
  nodes.forEach(({ menu, node }) => {
    const parent = menu.parentMenuId != null ? byId.get(menu.parentMenuId) : undefined
    if (parent) {
      parent.children = [...(parent.children ?? []), node]
    } else {
      roots.push(node)
    }
  })
  return roots
}

export function filterMenu(
  menus: MenuItemDef[],
  userLevel: UserLevel,
  activeModules: string[],
  activeFunctions: string[],
): MenuItemDef[] {
  return menus
    .filter((menu) => {
      const levelOk = userLevel <= menu.maxUserLevel
      const moduleOk = !menu.requiredModule || activeModules.includes(menu.requiredModule)
      const funcOk = !menu.requiredFunction || activeFunctions.includes(menu.requiredFunction)
      return levelOk && moduleOk && funcOk
    })
    .map((menu) => ({
      ...menu,
      children: menu.children
        ? filterMenu(menu.children, userLevel, activeModules, activeFunctions)
        : undefined,
    }))
}
